/// <amd-module name="Reporting/BoardsCommon/ReportTelemetry" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

// Area Identifier for reporting telemetry & traces
export const telemetryAreaName = "Analytics-Reports";

// Guidance - What should I use for telemetry?
// Happy path tracking  -> publishReportTelemetry
// 0 data scenario      -> publishEmptyReportTelemetry
// Error scenario       -> trace

/** An unhappy-path variant of publishReportTelemetry, for categorizing known, PM-relevant configuration problems, as distinct from error telemetry. */
export function publishEmptyReportTelemetry(pageContext: IVssPageContext, featureName: string, emptyStateReason: string): void {
    publishReportTelemetry(pageContext, featureName, { isEmptyState: true, emptyStateReason: emptyStateReason });
}

/** A simple associative array containing readily serializable details, as chosen by individual reports. */
export interface TelemetryDetailProps {
    [key: string]: string | number | boolean | undefined;
}

/**
 *  *Standard-form* telemetry for answering the following questions in a readily reportable fashion:
 *      1-How many users are seeing this report
 *      2-How many users are seeing the reports in empty state  (Use publishEmptyReportTelemetry() instead, for this case )
 *      3-What kind of configuration options are users employing
 */
export function publishReportTelemetry(pageContext: IVssPageContext, featureName: string, reportViewInfo: TelemetryDetailProps): void {
    const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    const telemetryProps = { isEmptyState: false, ...reportViewInfo };
    telemetryService.publishEvent(telemetryAreaName, featureName, telemetryProps);
}

/** An interface to define props that are recorded as part of teaching bubble usage */
export interface TeachingBubbleTelemetryProps {
    currentTeachingBubbleName: string;
    buttonAction: string;
}

/**
 *  Telemetry for recording clicks of teaching bubble and tutorial walkthrough
 *      1-Which report is the user on
 *      2-Which teaching bubble they are navigating to and from
 *      3-Which teaching bubble are they dismissing the tour from
 *      4-Are they clicking the Learn more link to visit our reports documentation
 */
export function publishTeachingBubbleTelemetry(
    pageContext: IVssPageContext,
    featureName: string,
    teachingBubbleTelemetry: TeachingBubbleTelemetryProps
): void {
    const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    const telemetryProps = { isEmptyState: false, ...teachingBubbleTelemetry };
    telemetryService.publishEvent(telemetryAreaName, featureName, telemetryProps);
}
